import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { auth } from '../../services/auth';
import { toolkitApi } from '../../services/toolkit';
import { CategorySelect } from './CategorySelect';
import { QuestionsForm } from './QuestionsForm';
import { IssuesSelect } from './IssuesSelect';
import { SendSetup } from './SendSetup';
import { ToneSelect } from './ToneSelect';
import { MessageEditor } from './MessageEditor';
import { SavedMessages } from './SavedMessages';

function StepTracker({ steps, current }) {
  return (
    <ol className="mb-8 flex items-center gap-2 text-xs font-semibold sm:text-sm">
      {steps.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        return (
          <React.Fragment key={label}>
            <li className="flex items-center gap-2">
              <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-full ${done ? 'bg-emerald-600 text-white' : active ? 'bg-white text-emerald-700 ring-2 ring-emerald-500 dark:bg-slate-800' : 'bg-slate-200 text-slate-500 dark:bg-slate-700'}`}>
                {done ? '✓' : n}
              </span>
              <span className={`hidden sm:inline ${done || active ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>{label}</span>
            </li>
            {i < steps.length - 1 && <li className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

function UpsellPanel() {
  return (
    <div className="mx-auto max-w-xl rounded-2xl border border-emerald-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800">
      <h2 className="text-xl font-bold text-slate-900 dark:text-white">
        Get it in writing before it becomes a dispute.
      </h2>
      <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
        The Get It In Writing toolkit turns your LeaseCheck findings into calm,
        professional emails and letters — deposits, fees, utilities, renewals,
        repairs, and move-out charges. Personalized with your details, edited
        in your own words.
      </p>
      <p className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
        Included with the Premium Plan.
      </p>
      <Link to="/legal?plan=premium"
        className="mt-5 inline-block rounded-lg bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
        Upgrade to Premium
      </Link>
    </div>
  );
}

export default function Toolkit() {
  const location = useLocation();
  const prefill = location.state?.prefill || null;
  const fromReport = !!prefill?.issues?.length;

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: auth.me, suspense: false, retry: false });

  const [config, setConfig] = useState(null);
  const [tab, setTab] = useState('new');
  const [step, setStep] = useState(1);
  const [categoryKey, setCategoryKey] = useState(fromReport ? 'lease_review_followup' : null);
  const [checkedIssues, setCheckedIssues] = useState(prefill?.checked ?? []);
  const [answers, setAnswers] = useState({});
  const [deliveryFormat, setDeliveryFormat] = useState('email');
  const [contact, setContact] = useState({});
  const [recipient, setRecipient] = useState({});
  const [tone, setTone] = useState(null);
  const [message, setMessage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    toolkitApi.getCategories().then(setConfig).catch(() => setConfig(false));
    toolkitApi.getContact().then((c) => setContact(c || {})).catch(() => {});
  }, []);

  if (!me || config === null) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;
  }
  const isPremium = me.plan_tier === 'premium' || me.is_superuser;
  if (!isPremium) return <UpsellPanel />;
  if (config === false) {
    return <p className="text-sm text-red-600">Couldn&apos;t load the toolkit — please refresh.</p>;
  }

  // Report entry: Issues → Send → Tone → Draft. Standalone: Issue type →
  // Details → Send → Tone → Draft.
  const STEPS = fromReport
    ? ['Items', 'Send & details', 'Tone', 'Your message']
    : ['Issue', 'Details', 'Send & details', 'Tone', 'Your message'];
  const stepName = STEPS[step - 1];

  const category = config.categories.find((c) => c.key === categoryKey);
  const categoryTitles = Object.fromEntries(config.categories.map((c) => [c.key, c.title]));

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      // Persist sender details for next time; blanks are fine.
      toolkitApi.putContact(contact).catch(() => {});
      const issues = fromReport ? checkedIssues.map((i) => prefill.issues[i]) : [];
      const msg = await toolkitApi.generate({
        category: categoryKey,
        tone,
        answers,
        issues,
        delivery_format: deliveryFormat,
        document_id: prefill?.documentId ?? null,
        ...recipient,
      });
      setMessage(msg);
      setStep(STEPS.length);
    } catch (e) {
      setError(e.response?.data?.detail || 'Generation failed — please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const reset = () => {
    setStep(1); setAnswers({}); setTone(null); setMessage(null);
    if (!fromReport) setCategoryKey(null);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Get It In Writing</h1>
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
          {[['new', 'New request'], ['saved', 'Saved requests']].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === key ? 'bg-white text-emerald-700 shadow-sm dark:bg-slate-700 dark:text-emerald-300' : 'text-slate-600 dark:text-slate-300'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'saved' ? (
        <SavedMessages categoryTitles={categoryTitles} />
      ) : (
        <div>
          <StepTracker steps={STEPS} current={step} />
          {error && (
            <p className="mb-4 rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-800 dark:bg-red-900/30 dark:text-red-200">
              {error}
            </p>
          )}
          {stepName === 'Items' && (
            <IssuesSelect issues={prefill.issues} checked={checkedIssues}
              setChecked={setCheckedIssues} onNext={() => setStep(step + 1)} />
          )}
          {stepName === 'Issue' && (
            <CategorySelect categories={config.categories.filter((c) => !c.report_only)}
              onSelect={(k) => { setCategoryKey(k); setStep(step + 1); }} />
          )}
          {stepName === 'Details' && category && (
            <QuestionsForm category={category} answers={answers} setAnswers={setAnswers}
              onBack={() => { setStep(step - 1); setCategoryKey(null); }}
              onNext={() => setStep(step + 1)} />
          )}
          {stepName === 'Send & details' && (
            <SendSetup
              deliveryFormat={deliveryFormat} setDeliveryFormat={setDeliveryFormat}
              contact={contact} setContact={setContact}
              recipient={recipient} setRecipient={setRecipient}
              onBack={() => setStep(step - 1)} onNext={() => setStep(step + 1)} />
          )}
          {stepName === 'Tone' && (
            <ToneSelect tones={config.tones} tone={tone} setTone={setTone}
              disclaimer={config.disclaimer} generating={generating}
              onBack={() => setStep(step - 1)} onGenerate={generate} />
          )}
          {stepName === 'Your message' && message && (
            <MessageEditor message={message} onChange={setMessage} onDone={reset} />
          )}
        </div>
      )}
    </div>
  );
}
